import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, apiPost, formatDateTime } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  FileText, Loader2, ArrowUp, Trash2, Clock, CheckCircle2, XCircle, Info, AlertTriangle, Play,
} from "lucide-react";
import { toast } from "sonner";

/**
 * ReportQueueCard
 * ---------------
 * Admin-only panel that surfaces the report-analysis queue on the System
 * Status page. Pulls GET /api/admin/reports/queue-status every 10 s while
 * the queue is active, stopping once it's empty. Provides two admin
 * actions:
 *   - Prioritise a symbol → bumps pending reports for that symbol to
 *     the front of the queue (POST /api/admin/reports/prioritise/{sym}).
 *   - Clear the queue → skips every pending row (POST .../clear-queue).
 *
 * The currently-running row is intentionally left untouched by the
 * "Clear" action — the worker will finish it naturally.
 */
const fmtElapsed = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem.toString().padStart(2, "0")}s` : `${rem}s`;
};

const fmtEta = (minutes) => {
  if (minutes == null) return "—";
  if (minutes < 1) return "< 1 min";
  if (minutes < 60) return `~${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const rem = Math.round(minutes % 60);
  return `~${h}h ${rem}m`;
};

const ReportQueueCard = () => {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clearBusy, setClearBusy] = useState(false);
  const [priorityBusy, setPriorityBusy] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [priorityInput, setPriorityInput] = useState("");
  const [startBusy, setStartBusy] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const s = await apiGet("/admin/reports/queue-status");
      setSnap(s);
      setError(null);
      return s;
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load queue");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll every 10 s while the queue is active. As soon as it drains
  // (no currently_analysing row AND empty queue), stop polling.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const s = await fetchStatus();
      const isActive = !!(s && (s.currently_analysing || (s.queue_length ?? 0) > 0));
      if (isActive && !cancelled) {
        pollRef.current = setTimeout(tick, 10000);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [fetchStatus]);

  const onClearQueue = async () => {
    setClearBusy(true);
    try {
      const res = await apiPost("/admin/reports/clear-queue");
      toast.success(`Queue cleared — ${res.cleared_count} report(s) skipped`);
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear queue");
    } finally {
      setClearBusy(false);
    }
  };

  const onStartAnalysis = async () => {
    setStartBusy(true);
    try {
      const res = await apiPost("/admin/reports/start-analysis");
      const unstuck = res?.unstuck_before_start || 0;
      toast.success(
        unstuck > 0
          ? `Analysis started — ${unstuck} stuck row(s) healed, worker picking up the queue`
          : "Analysis started — worker is picking up the queue"
      );
      // Re-poll immediately so the "Currently analysing" box lights up.
      setTimeout(fetchStatus, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start analysis");
    } finally {
      setStartBusy(false);
    }
  };

  const onPrioritise = async () => {
    const sym = priorityInput.trim().toUpperCase();
    if (!sym) {
      toast.error("Enter a CSE symbol (e.g. COMB.N0000 or COMB)");
      return;
    }
    setPriorityBusy(true);
    try {
      const res = await apiPost(`/admin/reports/prioritise/${encodeURIComponent(sym)}`);
      if (res.prioritised_count === 0) {
        toast.info(`No pending reports found for ${sym}`);
      } else {
        toast.success(`Prioritised ${res.prioritised_count} report(s) for ${sym}`);
      }
      setPriorityOpen(false);
      setPriorityInput("");
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to prioritise");
    } finally {
      setPriorityBusy(false);
    }
  };

  const curr = snap?.currently_analysing;
  const queue = snap?.queue || [];
  const queueLen = snap?.queue_length ?? 0;
  const isActive = !!(curr || queueLen > 0);

  return (
    <Card className="border bg-card" data-testid="report-queue-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Report Analysis Queue
            {isActive && (
              <Badge
                variant="outline"
                className="text-[10px] gap-1 border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]"
                data-testid="report-queue-live-badge"
              >
                <Loader2 className="h-3 w-3 animate-spin" />
                live
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="default"
              className="gap-1.5"
              data-testid="report-queue-start-button"
              onClick={onStartAnalysis}
              disabled={startBusy || queueLen === 0 || !!curr}
              title={
                curr
                  ? "A report is already being analysed — the worker will continue automatically"
                  : queueLen === 0
                    ? "Queue is empty"
                    : "Kick off Claude analysis on every queued report"
              }
            >
              {startBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              Start Analysis
            </Button>
            <Dialog open={priorityOpen} onOpenChange={setPriorityOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  data-testid="report-queue-prioritise-button"
                  disabled={!isActive}
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                  Prioritise
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="report-queue-prioritise-dialog">
                <DialogHeader>
                  <DialogTitle>Prioritise pending reports for a symbol</DialogTitle>
                  <DialogDescription>
                    Moves every pending (un-analysed, non-failed) report for the
                    entered CSE symbol to the front of the queue. Accepts both
                    the company code (e.g. <span className="font-mono">COMB</span>) and
                    the full symbol (<span className="font-mono">COMB.N0000</span>).
                  </DialogDescription>
                </DialogHeader>
                <Input
                  value={priorityInput}
                  onChange={(e) => setPriorityInput(e.target.value)}
                  placeholder="e.g. COMB.N0000"
                  className="font-mono"
                  data-testid="report-queue-prioritise-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onPrioritise();
                  }}
                />
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setPriorityOpen(false)} disabled={priorityBusy}>
                    Cancel
                  </Button>
                  <Button
                    onClick={onPrioritise}
                    disabled={priorityBusy}
                    className="gap-1.5"
                    data-testid="report-queue-prioritise-confirm"
                  >
                    {priorityBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowUp className="h-3.5 w-3.5" />}
                    Prioritise
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-[hsl(var(--destructive))]/40 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                  data-testid="report-queue-clear-button"
                  disabled={!isActive || queueLen === 0}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Clear Queue
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="report-queue-clear-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel every pending report?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This marks all {queueLen} queued report(s) as <span className="font-mono">skipped</span>.
                    The row stays in the database and can be re-triggered later. The
                    currently-running report will finish naturally.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={clearBusy}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onClearQueue}
                    disabled={clearBusy}
                    className="gap-1.5"
                    data-testid="report-queue-clear-confirm"
                  >
                    {clearBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    Clear {queueLen} reports
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !snap && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading queue…
          </div>
        )}
        {error && (
          <div className="text-xs text-[hsl(var(--destructive))]">
            {error}
          </div>
        )}
        {/* Large-queue warning banner — fires above 50 pending rows.
            Large queues blow through the nightly Claude budget *and*
            fill the /app disk with PDFs faster than the 02:00 SLT
            cleanup can evict them, so we surface a loud alert with
            a one-click "Clear Queue" shortcut. */}
        {queueLen > 50 && (
          <div
            className="flex items-start gap-2 rounded-md border border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs"
            data-testid="report-queue-size-warning"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--warning))]" />
            <div className="space-y-0.5 flex-1">
              <div className="font-semibold text-foreground">
                Large queue detected: {queueLen} reports queued (~{fmtEta(snap?.estimated_total_minutes)})
              </div>
              <div className="text-muted-foreground">
                Consider clearing the queue and re-queuing only priority stocks.
                Each report costs ~$0.15 on Claude Sonnet 4.5.
              </div>
            </div>
          </div>
        )}
        {/* Currently analysing */}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
            Currently analysing
          </div>
          {curr ? (
            <div
              className="rounded-md border border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/5 px-3 py-2.5 space-y-1"
              data-testid="report-queue-current"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <FileText className="h-3.5 w-3.5 text-[hsl(var(--info))] shrink-0" />
                <span className="text-sm font-medium" data-testid="report-queue-current-company">
                  {curr.company}
                </span>
                <Badge variant="outline" className="text-[10px] uppercase font-mono">
                  {curr.symbol}
                </Badge>
                <Badge variant="secondary" className="text-[10px] capitalize">
                  {curr.report_type || "report"}
                </Badge>
                {curr.period && (
                  <span className="text-xs text-muted-foreground">· {curr.period}</span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Running for <span className="font-semibold text-foreground">{fmtElapsed(curr.elapsed_seconds)}</span>
                  <Loader2 className="h-3 w-3 animate-spin text-[hsl(var(--info))]" />
                </span>
                {curr.is_chunked && (
                  <span>
                    Chunks <span className="font-semibold text-foreground">
                      {curr.chunks_completed ?? 0}/{curr.chunks_total ?? "?"}
                    </span>
                  </span>
                )}
                {curr.is_vision && (
                  <Badge variant="outline" className="text-[10px]">Vision</Badge>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              No report currently being analysed.
            </div>
          )}
        </div>

        {/* Queue list */}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center justify-between">
            <span>
              Queue ({queueLen} {queueLen === 1 ? "waiting" : "waiting"})
            </span>
            {queueLen > 0 && (
              <span className="normal-case text-muted-foreground">
                Est. completion: {fmtEta(snap?.estimated_total_minutes)}
              </span>
            )}
          </div>
          {queueLen === 0 ? (
            <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              Queue is empty.
            </div>
          ) : (
            <ol className="space-y-1" data-testid="report-queue-list">
              {queue.slice(0, 8).map((q) => (
                <li
                  key={q.report_id}
                  className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-2.5 py-1.5 text-xs"
                  data-testid={`report-queue-item-${q.position}`}
                >
                  <span className="font-mono text-muted-foreground w-6 tabular-nums">{q.position}.</span>
                  <span className="font-medium truncate max-w-[220px]" title={q.company}>
                    {q.company}
                  </span>
                  <Badge variant="outline" className="text-[9px] font-mono">{q.symbol}</Badge>
                  <span className="text-muted-foreground capitalize">
                    · {q.report_type || "report"}
                  </span>
                  {q.period && (
                    <span className="text-muted-foreground">· {q.period}</span>
                  )}
                  {q.is_prioritised && (
                    <Badge
                      variant="outline"
                      className="text-[9px] gap-1 border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]"
                    >
                      <ArrowUp className="h-2.5 w-2.5" />
                      prioritised
                    </Badge>
                  )}
                </li>
              ))}
              {queueLen > 8 && (
                <li className="text-[11px] text-muted-foreground pl-8">
                  … {queueLen - 8} more in the queue
                </li>
              )}
            </ol>
          )}
        </div>

        {/* Daily stats */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t">
          <div className="flex items-center gap-2 text-xs">
            <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
            <span className="text-muted-foreground">Completed today:</span>
            <span className="font-semibold" data-testid="report-queue-completed-today">
              {snap?.completed_today ?? 0}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <XCircle className="h-3.5 w-3.5 text-[hsl(var(--destructive))]" />
            <span className="text-muted-foreground">Failed today:</span>
            <span className="font-semibold" data-testid="report-queue-failed-today">
              {snap?.failed_today ?? 0}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Info className="h-3 w-3" />
          Queue refreshes every 10 seconds while active. Last fetched {snap?.now ? formatDateTime(snap.now) : "…"}
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportQueueCard;
